import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Rocket, Gamepad2, Boxes, Puzzle, Zap, Layers } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconFor = (genre) => {
  const map = { platformer: Gamepad2, fps: Zap, puzzle: Puzzle, simulation: Layers, rpg: Boxes };
  return map[genre] || Sparkles;
};

const difficultyColor = {
  beginner: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  intermediate: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  advanced: 'text-rose-400 border-rose-500/30 bg-rose-500/10',
};

export default function Templates() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/templates`);
        setTemplates(res.data.templates || []);
      } catch (e) {
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleUseTemplate = async (tpl) => {
    if (!isAuthenticated) {
      toast.info('Sign in to fork a template');
      navigate('/login');
      return;
    }
    setBusy(tpl.template_id);
    try {
      const res = await axios.post(`${API}/templates/${tpl.template_id}/use`, {}, { withCredentials: true });
      toast.success('Project created from template');
      navigate(`/editor/${res.data.project_id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to fork template';
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-zinc-400" data-testid="templates-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Rocket className="w-4 h-4 text-[#06B6D4]" />
            <span className="hidden sm:inline">Start faster with pre-built game seeds</span>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-6 py-10 sm:py-14">
        <div className="mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#06B6D4]" />
            <span className="text-[11px] font-mono tracking-widest uppercase text-[#06B6D4]">Game Templates</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3">Fork a template. Ship in minutes.</h1>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl">
            Pick a hand-crafted starter — platformer, shooter, puzzle, endless runner or open sandbox. Fork it, tweak it, publish it.
            Every template is a fully playable game data structure you can edit in the visual editor.
          </p>
        </div>

        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...templates].sort((a, b) => (b.promoted ? 1 : 0) - (a.promoted ? 1 : 0)).map((tpl) => {
              const Icon = iconFor(tpl.genre);
              const diff = difficultyColor[tpl.difficulty] || difficultyColor.beginner;
              return (
                <div
                  key={tpl.template_id}
                  className="group rounded-2xl border border-[#1E1E24] bg-[#0A0A0B] overflow-hidden hover:border-[#06B6D4]/40 hover:-translate-y-0.5 transition-all"
                  data-testid={`template-card-${tpl.template_id}`}
                >
                  {tpl.thumbnail ? (
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={tpl.thumbnail}
                        alt={tpl.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        data-testid={`template-cover-${tpl.template_id}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        {tpl.promoted && (
                          <span className="px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-amber-500/90 text-black font-bold" data-testid={`template-promoted-badge-${tpl.template_id}`}>★ Studio Pick</span>
                        )}
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-black/60 backdrop-blur text-[#67E8F9] border border-[#06B6D4]/30">{tpl.genre}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="relative aspect-[16/9] bg-gradient-to-br from-[#0E1B26] to-[#0A0A0B] flex items-center justify-center">
                      <Icon className="w-14 h-14 text-[#06B6D4]/50" />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-black/60 text-[#67E8F9] border border-[#06B6D4]/30">{tpl.genre}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg font-black text-white tracking-tight leading-snug">{tpl.name}</h3>
                      <span className={`shrink-0 px-2 py-0.5 text-[10px] font-mono uppercase border rounded-sm ${diff}`}>
                        {tpl.difficulty || 'beginner'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 mb-4 leading-relaxed line-clamp-2">{tpl.description}</p>
                    <Button
                      onClick={() => handleUseTemplate(tpl)}
                      disabled={busy === tpl.template_id}
                      size="sm"
                      className="w-full bg-white text-black hover:bg-zinc-200 rounded-full font-bold"
                      data-testid={`template-use-${tpl.template_id}`}
                    >
                      {busy === tpl.template_id ? 'Forking…' : (<><Sparkles className="w-3.5 h-3.5 mr-1.5" />Start from this template</>)}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
